import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { createElement } from "react";
import { config } from "../config";

export interface CompanyInfo {
  id: string;
  brandName: string;
  botUsername: string | null;
  miniAppDomain: string;
}

interface CompanyState {
  isLoading: boolean;
  company: CompanyInfo | null;
  error: string | null;
}

const CompanyContext = createContext<CompanyState | null>(null);

export const useCompany = (): CompanyState => {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used within CompanyProvider");
  return ctx;
};

/** Brand name with a sensible fallback during initial load. */
export const useBrandName = (): string => {
  const { company } = useCompany();
  return company?.brandName || "Pufferfish";
};

const resolveHostname = (): string => {
  // Local dev: fall back to a configured slug so we can develop without DNS
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname.endsWith(".local")) {
    return import.meta.env.VITE_DEV_COMPANY_DOMAIN || hostname;
  }
  return hostname;
};

const fetchCompanyByDomain = async (hostname: string): Promise<CompanyInfo | null> => {
  const response = await fetch(config.hasuraGraphqlUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      query: `
        query GetCompanyByDomain($domain: String!) {
          companies(where: { mini_app_domain: { _eq: $domain } }, limit: 1) {
            id
            brand_name
            telegram_bot_username
            mini_app_domain
          }
        }
      `,
      variables: { domain: hostname },
    }),
  });
  const result = await response.json();
  const company = result?.data?.companies?.[0];
  if (!company) return null;
  return {
    id: company.id,
    brandName: company.brand_name,
    botUsername: company.telegram_bot_username,
    miniAppDomain: company.mini_app_domain,
  };
};

export const CompanyProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<CompanyState>({
    isLoading: true,
    company: null,
    error: null,
  });

  useEffect(() => {
    const hostname = resolveHostname();
    fetchCompanyByDomain(hostname)
      .then((company) => {
        if (!company) {
          setState({
            isLoading: false,
            company: null,
            error: `Domain "${hostname}" is not configured for any organization.`,
          });
          return;
        }
        setState({ isLoading: false, company, error: null });
        // Imperatively set the document title to the brand name
        document.title = company.brandName;
      })
      .catch((err) => {
        setState({
          isLoading: false,
          company: null,
          error: err instanceof Error ? err.message : "Failed to resolve company",
        });
      });
  }, []);

  return createElement(CompanyContext.Provider, { value: state }, children);
};
